import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ClickAwayListener, Grow, MenuItem, MenuList, Button as MuiButton, ButtonGroup as MuiButtonGroup, Paper, Popper, } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useKeyboardShortcut } from '@zurich/common/hooks';
import { styled } from '../../utils';
import { Icon } from '../Icon';
// TODO: initialIndex doesn't work
export function SplitButton({ dataName, className, variant, color, startIcon, options, handleClick, handleOptionValue, disabled, loading, initialIndex, size, optionTextSize, placeHolderText, shortcutParams = { key: '', translationPrefix: '', dependency: false }, classNameOptions, }) {
    const [open, setOpen] = useState(false);
    const [clicked, setClicked] = useState(false);
    const anchorRef = useRef(null);
    // memoization due to the filter - but do we really have many options passed in?
    const { firstIndex, enabledOptions, firstEnabledIndex } = useMemo(() => {
        const enabledOptions = options?.filter((o) => !o.disabled) ?? [];
        return {
            firstIndex: options && options.length > 0 ? options[0].value : null,
            enabledOptions,
            firstEnabledIndex: enabledOptions.length > 0 ? enabledOptions[0].value : null,
        };
    }, [options]);
    const [selectedIndex, setSelectedIndex] = useState(initialIndex ?? firstEnabledIndex ?? firstIndex ?? null);
    useEffect(() => {
        if (handleOptionValue && enabledOptions.length > 0 && selectedIndex === 0)
            handleOptionValue(enabledOptions[0]);
    }, [enabledOptions, handleOptionValue, selectedIndex]);
    useEffect(() => {
        // update default action if enabled options change
        if (firstEnabledIndex || firstEnabledIndex === 0) {
            setSelectedIndex(firstEnabledIndex);
        }
    }, [firstEnabledIndex, setSelectedIndex]);
    const handleMenuItemClick = (event, index) => {
        if (!clicked) {
            setClicked(true);
        }
        setSelectedIndex(index);
        setOpen(false);
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleClose = (event) => {
        if (anchorRef.current &&
            event.target instanceof Element &&
            anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };
    const handleClickCallback = useCallback(() => (selectedIndex === null ? undefined : handleClick(selectedIndex)), [handleClick, selectedIndex]);
    const isDisabled = disabled || enabledOptions.length === 0 || loading;
    const ariaShortcutKeys = useKeyboardShortcut({
        ...shortcutParams,
        callback: handleClickCallback,
    });
    return (_jsxs(_Fragment, { children: [_jsxs(ButtonGroupStyled, { className: className, variant: variant, color: color, size: size, ref: anchorRef, disabled: isDisabled === true, "aria-keyshortcuts": ariaShortcutKeys, children: [placeHolderText && !clicked ? (_jsx(MuiButton, { startIcon: startIcon, color: color, size: size, className: classNameOptions, children: placeHolderText })) : (_jsx(MuiButton, { startIcon: startIcon, color: color, size: size, onClick: handleClickCallback, className: classNameOptions, children: options?.find((o) => o.value === selectedIndex)?.label })), enabledOptions.length > 1 && (_jsx(MuiButton, { "data-name": dataName, color: color, size: size, "aria-controls": open ? 'split-button-menu' : undefined, "aria-expanded": open ? 'true' : undefined, "aria-haspopup": "menu", onClick: handleToggle, disabled: isDisabled, children: _jsx(Icon, { variant: "arrow-down-2", fontSize: "small" }) }))] }), _jsx(PopperStyled, { open: open, anchorEl: anchorRef.current, role: undefined, transition: true, disablePortal: true, children: ({ TransitionProps, placement }) => (_jsx(Grow, { ...TransitionProps, style: {
                        transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                    }, children: _jsx(Paper, { children: _jsx(ClickAwayListener, { onClickAway: handleClose, children: _jsx(MenuList, { id: "split-button-menu", children: enabledOptions.map((option, index) => (_jsx(MenuItem, { selected: option.value === selectedIndex, onClick: (event) => {
                                        handleMenuItemClick(event, option.value);
                                        if (handleOptionValue) {
                                            handleOptionValue(option);
                                        }
                                    }, children: _jsx(StyledSpan, { size: optionTextSize ?? size ?? 'medium', children: option.label }) }, index))) }) }) }) })) })] }));
}
const ButtonGroupStyled = styled(MuiButtonGroup)({
    boxShadow: 'none',
});
const PopperStyled = styled(Popper)({
    zIndex: 10,
});
const StyledSpan = styled('span')(({ size }) => ({
    fontSize: size,
}));
